import React, { useState, useEffect } from "react";
import { MessageCircle, Share2, ThumbsUp, Award, Clock, Send, PlusCircle, Image, FileText, Link, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const WorkvivoPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPostMessage, setNewPostMessage] = useState("");
  const [showPostOptions, setShowPostOptions] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postingMessage, setPostingMessage] = useState(false);
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    // Fetch posts from Workvivo
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        // In production, this would be an actual API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Mock data for demonstration
        const mockPosts = [
          {
            id: 1,
            author: {
              id: 101,
              name: "Rofaiel Tadrous",
              profilePic: "/images/Rofa.jpeg",
              role: "Senior Engineer"
            },
            content: "I'm excited to share that our Recognition App is now integrated with Workvivo! This allows us to share achievements and celebrate success across the entire organization. 🎉🚀",
            timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
            likes: 15,
            comments: 3,
            shares: 2,
            recognition: null, // Regular post without recognition
            liked: false,
            commentsList: [
              {
                id: 1001,
                author: {
                  name: "Michael Baskhairoun",
                  profilePic: "/images/Michael.jpg",
                },
                content: "Great job on the integration! This will help us spread recognition across teams.",
                timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
              },
              {
                id: 1002,
                author: {
                  name: "Caroline Smith",
                  profilePic: "/images/Caroline.jpg",
                },
                content: "Looking forward to using this for our team's achievements!",
                timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
              }
            ]
          },
          {
            id: 2,
            author: {
              id: 102,
              name: "Raghav Bhalla",
              profilePic: "/images/Raghav.jpg",
              role: "Design Lead"
            },
            content: "Sharing this recognition from our internal app:",
            timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
            likes: 24,
            comments: 5,
            shares: 1,
            liked: false,
            recognition: {
              recognizedUser: { 
                name: "Michael Baskhairoun", 
                profilePic: "/images/Michael.jpg" 
              },
              title: "Innovation Champion",
              message: "Your creative solution to our database performance issue saved us hours of work. Great thinking outside the box! 🚀",
              type: "Achievement"
            },
            commentsList: [
              {
                id: 2001,
                author: {
                  name: "Caroline Smith",
                  profilePic: "/images/Caroline.jpg",
                },
                content: "Well deserved! Great work 👏",
                timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000),
              }
            ]
          },
          {
            id: 3,
            author: {
              id: 103,
              name: "Michael Baskhairoun",
              profilePic: "/images/Michael.jpg",
              role: "Product Manager"
            },
            content: "I'd like to recognize our amazing team for their hard work this quarter:",
            timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
            likes: 18,
            comments: 4,
            shares: 0,
            liked: false,
            recognition: {
              recognizedUser: { 
                name: "Rofaiel Tadrous", 
                profilePic: "/images/Rofa.jpeg" 
              },
              title: "Team Player Award",
              message: "Thanks for stepping up and helping the team during crunch time! Your support made all the difference in meeting our sprint goals. 🙌",
              type: "Appreciation"
            },
            commentsList: []
          }
        ];

        setPosts(mockPosts);
      } catch (err) {
        setError("Failed to load posts from Workvivo");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Format relative time 
  const getRelativeTime = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Handle post submission
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPostMessage.trim()) return;
    
    setPostingMessage(true);
    
    try {
      // In production, this would be an actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create new post and add it to the list
      const newPost = {
        id: Date.now(),
        author: {
          id: 101,
          name: "Rofaiel Tadrous",
          profilePic: "/images/Rofa.jpeg",
          role: "Senior Engineer"
        },
        content: newPostMessage,
        timestamp: new Date(),
        likes: 0,
        comments: 0,
        shares: 0,
        liked: false,
        recognition: null,
        commentsList: []
      };
      
      setPosts([newPost, ...posts]);
      setNewPostMessage("");
    } catch (err) {
      console.error("Failed to post message:", err);
      // Show error toast or notification
    } finally {
      setPostingMessage(false);
      setShowPostOptions(false);
    }
  };

  // Handle comment on a post
  const handleComment = (postId) => {
    setSelectedPost(postId === selectedPost ? null : postId);
  };
  
  // Handle like on a post
  const handleLike = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const newLikedState = !post.liked;
        return {
          ...post,
          liked: newLikedState,
          likes: newLikedState ? post.likes + 1 : post.likes - 1
        };
      }
      return post;
    }));
  };
  
  // Handle share
  const handleShare = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          shares: post.shares + 1
        };
      }
      return post;
    }));
    
    // Show share notification
    alert("Post shared successfully!");
  };
  
  // Handle adding a comment
  const handleAddComment = (postId) => {
    if (!commentText.trim()) return;
    
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const newComment = {
          id: Date.now(),
          author: {
            name: "Rofaiel Tadrous",
            profilePic: "/images/Rofa.jpeg"
          },
          content: commentText,
          timestamp: new Date()
        };
        
        return {
          ...post,
          comments: post.comments + 1,
          commentsList: [...post.commentsList, newComment]
        };
      }
      return post;
    }));
    
    setCommentText("");
  };

  return (
    <div className="flex flex-col p-4 md:p-6 bg-gray-50 min-h-screen w-full">
      {/* New Post Input */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
        <div className="p-4 org-gradient">
          <h2 className="text-xl font-bold text-white">Share on Workvivo</h2>
        </div>
        
        <div className="p-4">
          <form onSubmit={handlePostSubmit}>
            <div className="flex items-start gap-3">
              <img 
                src={process.env.PUBLIC_URL + "/images/Rofa.jpeg"}
                alt="Your Profile" 
                className="w-10 h-10 rounded-full object-cover"
              />
              
              <div className="flex-1">
                <div className="relative">
                  <textarea
                    value={newPostMessage}
                    onChange={(e) => setNewPostMessage(e.target.value)}
                    placeholder="Share a recognition or message on Workvivo..."
                    className="w-full p-3 pr-12 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-h-[100px]"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPostOptions(!showPostOptions)}
                    className="absolute bottom-3 right-3 text-purple-500 hover:text-purple-700"
                  >
                    <PlusCircle className="w-6 h-6" />
                  </button>
                </div>
                
                <AnimatePresence>
                  {showPostOptions && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-2 bg-gray-50 p-3 rounded-2xl flex items-center gap-4"
                    >
                      <button type="button" className="flex items-center gap-1 text-gray-600 hover:text-purple-600">
                        <Image className="w-5 h-5" />
                        <span className="text-sm">Photo</span>
                      </button>
                      <button type="button" className="flex items-center gap-1 text-gray-600 hover:text-purple-600">
                        <Award className="w-5 h-5" />
                        <span className="text-sm">Recognition</span>
                      </button>
                      <button type="button" className="flex items-center gap-1 text-gray-600 hover:text-purple-600">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm">Article</span>
                      </button>
                      <button type="button" className="flex items-center gap-1 text-gray-600 hover:text-purple-600">
                        <Link className="w-5 h-5" />
                        <span className="text-sm">Link</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <div className="mt-4 flex justify-end">
                  <button 
                    type="submit"
                    disabled={!newPostMessage.trim() || postingMessage}
                    className={`px-4 py-2 rounded-full flex items-center gap-2 ${
                      !newPostMessage.trim() || postingMessage
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'org-gradient text-white'
                    }`}
                  >
                    {postingMessage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Posting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Post to Workvivo</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-2xl shadow-md p-8 flex flex-col items-center justify-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
          <p className="text-gray-600">Loading posts from Workvivo...</p>
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="bg-white rounded-2xl shadow-md p-8 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <MessageCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Unable to load posts</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !error && posts.length === 0 && (
        <div className="bg-white rounded-2xl shadow-md p-8 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <MessageCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No posts yet</h3>
          <p className="text-gray-600 mb-4">Be the first to share a recognition on Workvivo!</p>
        </div>
      )}
      
      {/* Posts List */}
      {!loading && !error && posts.length > 0 && (
        <div className="space-y-6">
          {posts.map(post => (
            <div key={post.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
              {/* Post Header */}
              <div className="p-4 pb-2">
                <div className="flex items-center">
                  <img 
                    src={process.env.PUBLIC_URL + post.author.profilePic}
                    alt={post.author.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-purple-500"
                  />
                  
                  <div className="ml-3 flex-1">
                    <p className="font-semibold text-gray-900">{post.author.name}</p>
                    <div className="flex items-center text-xs text-gray-500">
                      <span>{getRelativeTime(post.timestamp)}</span>
                      <span className="mx-1">•</span>
                      <span>{post.author.role}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Workvivo
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Post Content */}
              <div className="px-4 py-2">
                <p className="text-gray-700">{post.content}</p>
                
                {/* Recognition Card (if exists) */}
                {post.recognition && (
                  <div className="mt-4 border border-gray-200 rounded-2xl p-4 bg-gray-50">
                    <div className="flex items-start">
                      <div>
                        <img 
                          src={process.env.PUBLIC_URL + post.recognition.recognizedUser.profilePic}
                          alt={post.recognition.recognizedUser.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                        />
                      </div>
                      
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <Award className={`w-5 h-5 mr-2 ${
                            post.recognition.type === "Recognition" 
                              ? "text-blue-500" 
                              : post.recognition.type === "Appreciation" 
                                ? "text-purple-500"
                                : "text-green-500"
                          }`} />
                          <h3 className="font-bold text-gray-900">{post.recognition.title}</h3>
                        </div>
                        
                        <p className="text-sm font-medium text-gray-600 mt-1">
                          To: {post.recognition.recognizedUser.name}
                        </p>
                        
                        <p className="mt-2 text-gray-700">
                          {post.recognition.message}
                        </p>
                        
                        <div className="mt-2 text-xs text-gray-500 flex items-center">
                          <span className={`px-2 py-1 rounded-full ${
                            post.recognition.type === "Recognition" 
                              ? "bg-blue-100 text-blue-700" 
                              : post.recognition.type === "Appreciation" 
                                ? "bg-purple-100 text-purple-700"
                                : "bg-green-100 text-green-700"
                          }`}>
                            {post.recognition.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Post Stats */}
                <div className="flex items-center text-sm text-gray-500 mt-4 mb-1">
                  <div className="flex items-center mr-4">
                    <ThumbsUp className="w-4 h-4 text-gray-400 mr-1" />
                    <span>{post.likes} Likes</span>
                  </div>
                  <div className="flex items-center mr-4">
                    <MessageCircle className="w-4 h-4 text-gray-400 mr-1" />
                    <span>{post.comments} Comments</span>
                  </div>
                  <div className="flex items-center">
                    <Share2 className="w-4 h-4 text-gray-400 mr-1" />
                    <span>{post.shares} Shares</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="border-t border-gray-100 grid grid-cols-3 divide-x divide-gray-100">
                <button 
                  className={`py-3 flex justify-center items-center gap-2 transition-colors ${
                    post.liked 
                      ? 'text-blue-500' 
                      : 'text-gray-600 hover:text-blue-500 hover:bg-gray-50'
                  }`}
                  onClick={() => handleLike(post.id)}
                >
                  <ThumbsUp className={`w-5 h-5 ${post.liked ? 'fill-blue-500' : ''}`} />
                  <span className="font-medium text-sm">Like</span>
                </button>
                <button 
                  className="py-3 flex justify-center items-center gap-2 text-gray-600 hover:text-purple-500 hover:bg-gray-50 transition-colors"
                  onClick={() => handleComment(post.id)}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="font-medium text-sm">Comment</span>
                </button>
                <button 
                  className="py-3 flex justify-center items-center gap-2 text-gray-600 hover:text-green-500 hover:bg-gray-50 transition-colors"
                  onClick={() => handleShare(post.id)}
                >
                  <Share2 className="w-5 h-5" />
                  <span className="font-medium text-sm">Share</span>
                </button>
              </div>
              
              {/* Comments Section */}
              {(selectedPost === post.id || post.commentsList.length > 0) && (
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                  {/* Existing Comments */}
                  {post.commentsList.length > 0 && (
                    <div className="space-y-4 mb-4">
                      {post.commentsList.map(comment => (
                        <div key={comment.id} className="flex items-start">
                          <img 
                            src={process.env.PUBLIC_URL + comment.author.profilePic}
                            alt={comment.author.name} 
                            className="w-8 h-8 rounded-full mr-3 object-cover"
                          />
                          <div className="flex-1">
                            <div className="bg-white p-3 rounded-2xl shadow-sm">
                              <p className="font-medium text-sm text-gray-900">{comment.author.name}</p>
                              <p className="text-sm text-gray-700">{comment.content}</p>
                            </div>
                            <div className="flex items-center mt-1 text-xs text-gray-500">
                              <button className="hover:text-gray-700">Like</button>
                              <span className="mx-2">•</span>
                              <span>{getRelativeTime(comment.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Comment Input */}
                  <div className="flex items-center">
                    <img 
                      src={process.env.PUBLIC_URL + "/images/Rofa.jpeg"} 
                      alt="Your Avatar" 
                      className="w-8 h-8 rounded-full mr-3 object-cover"
                    />
                    <div className="flex-1 relative">
                      <input 
                        type="text" 
                        placeholder="Write a comment..." 
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddComment(post.id);
                          }
                        }}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <button 
                        className="absolute right-3 top-2 text-purple-500"
                        onClick={() => handleAddComment(post.id)}
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkvivoPosts;